/**
 * Source discovery — decide where live ADS-B and server data come from.
 *
 * Three deployment scenarios:
 *
 *  A) Bundled on the Pi (lighttpd serves us at http://pifeeder.local:8080/).
 *     Same-origin → /data/aircraft.json and /pifeeder/info are reachable
 *     without CORS. Server data (METAR/NOTAM/AIRMET) goes to
 *     VITE_PIFEEDER_API over HTTPS — HTTP→HTTPS is allowed.
 *
 *  B) Served from Cloud Run / Pages (https://app.pifeeder.xxx). LAN Pi is
 *     UNREACHABLE from here (Mixed Content rule blocks http://pifeeder.local
 *     from any HTTPS origin in 2026). So we just go through the API for
 *     everything; no live ADS-B.
 *
 *  C) Embedded in pi-feeder-app (Flutter InAppWebView). Native side pushes
 *     ADS-B via window.pifeederBridge; we read API for the rest.
 *
 * Detection is by `window.location.hostname` plus a one-shot probe of
 * /pifeeder/info to confirm we really are on a Pi.
 */

import { atom } from "nanostores";

export type Source =
  | { kind: "app" } // embedded in Flutter
  | { kind: "local"; baseUrl: string; info?: PifeederInfo } // same-origin Pi bundle
  | { kind: "api"; baseUrl: string } // remote HTTPS API only
  | { kind: "dev-mock" } // local vite dev server, mock Pi-side data only
  | { kind: "mock" }; // nothing, fall back to mock

export interface PifeederInfo {
  station: { name?: string; lat: number | null; lon: number | null };
  signal: { dB: number; online: boolean; trackedCount?: number };
  sdr?: string;
  trackedCount?: number;
  uptimeSec?: number;
  cpuTempC?: number;
  diskFreeMb?: number;
  firmware?: string;
}

export const $source = atom<Source>({ kind: "mock" });

const PROBE_TIMEOUT_MS = 1500;

/** Heuristic: hostname suggests we're being served from a Pi on the LAN. */
export function looksLikeLanHost(hostname: string): boolean {
  if (hostname.endsWith(".local")) return true;
  if (hostname === "pifeeder") return true; // some routers strip .local
  if (/^10\./.test(hostname)) return true;
  if (/^192\.168\./.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return true;
  // Localhost dev (vite preview on a Pi while developing)
  if (hostname === "localhost" || hostname === "127.0.0.1") return false;
  return false;
}

async function probeJson<T>(url: string, timeoutMs = PROBE_TIMEOUT_MS): Promise<T | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: ctrl.signal, cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

export async function detectSource(): Promise<Source> {
  // 1) Flutter embedding always wins.
  if ((window as unknown as { __pifeederIsApp?: boolean }).__pifeederIsApp) {
    return { kind: "app" };
  }

  // 2) Same-origin local Pi (the common case). Lighttpd serves us; /pifeeder/info
  //    is on the same origin so this works without CORS or Mixed Content.
  if (looksLikeLanHost(window.location.hostname)) {
    const info = await probeJson<PifeederInfo>("/pifeeder/info");
    if (info) {
      return { kind: "local", baseUrl: window.location.origin, info };
    }
  }

  // 3) Local dev (`vite dev`): no Pi reachable, but the developer wants to
  //    iterate on the SPA. Spin up a Pi-side mock (live aircraft + signal)
  //    while DB-backed endpoints (airport / weather / NOTAMs / aircraft
  //    registry) keep hitting the real Cloud Run API. `import.meta.env.DEV`
  //    is a compile-time constant — Vite replaces it with `false` in
  //    `vite build`, so this branch is dead-code-eliminated and dev-mock.ts
  //    is never bundled into a production artifact.
  if (import.meta.env.DEV) {
    return { kind: "dev-mock" };
  }

  // 4) Cloud / public hosting. Live ADS-B is unreachable; route everything
  //    through the API.
  const remote = (import.meta.env.VITE_PIFEEDER_API as string | undefined)?.replace(/\/$/, "");
  if (remote) {
    return { kind: "api", baseUrl: remote };
  }

  return { kind: "mock" };
}

/**
 * Resolve and publish the source. Re-probes every 30 s so a Pi powering up
 * later in the session gets picked up automatically.
 */
export async function startDiscovery(): Promise<void> {
  const update = async () => {
    const next = await detectSource();
    const cur = $source.get();
    if (cur.kind !== next.kind || ("baseUrl" in cur && "baseUrl" in next && cur.baseUrl !== next.baseUrl)) {
      $source.set(next);
    } else if (next.kind === "local" && cur.kind === "local") {
      $source.set(next); // keep info fresh
    }
  };
  await update();
  setInterval(update, 30_000);
}
